/* eslint-disable jsx-a11y/label-has-associated-control */
import { Button } from '@components/Button';
import { Match, Warehouse } from '@views/WarehouseDetails/WarehouseDetails';
import {
  BillingCycle,
  Invoice,
  InvoiceAdditionalService,
  VAT_RATE,
} from './invoiceTypes';

export type InvoiceTableEditSectionProps = {
  warehouse: Warehouse | undefined;
  handleAdditionalServiceDescriptionChange: (value: string, id: string) => void;
  additionalServices: InvoiceAdditionalService[];
  setAdditionalServices: React.Dispatch<
    React.SetStateAction<InvoiceAdditionalService[]>
  >;
  setPalletsProvisionedQuantity: React.Dispatch<React.SetStateAction<number>>;
  palletsProvisionedCost: number;
  palletsProvisionedVat: number;
  setSurchargesAppliedQuantity: React.Dispatch<React.SetStateAction<number>>;
  surchargesAppliedCost: number;
  surchargesAppliedVat: number;
  setOtherAmount: React.Dispatch<React.SetStateAction<number>>;
  otherAmountVat: number;
  handleDeleteService: (id: string) => void;
  manHours: number;
  setManHours: React.Dispatch<React.SetStateAction<number>>;
  auxiliaryServicesPrice: number;
  setAuxiliaryServicesPrice: React.Dispatch<React.SetStateAction<number>>;
};

export function InvoiceTableEditSection({
  billingCycle,
  invoice,
  match,
  ...props
}: {
  billingCycle: BillingCycle;
  invoice: Invoice;
  match: Match | undefined;
} & InvoiceTableEditSectionProps) {
  const vatForService = (service: InvoiceAdditionalService): string => {
    return service.amount
      ? ((service.amount * VAT_RATE) / 100).toFixed(2)
      : '-';
  };

  return (
    <>
      {!!props.auxiliaryServicesPrice && (
        <tr className='hover:bg-gray-50'>
          <td className='px-4 py-4 text-sm text-gray-500'>
            {invoice.lines.length + 1}
          </td>
          <td className='px-4 py-4 text-sm text-gray-900'>
            <div className='flex items-center justify-between gap-4'>
              <span>Auxiliary Services</span>
              <div className='flex items-center gap-2'>
                <span className='text-gray-500'>for</span>
                <label className='sr-only' htmlFor='manHours'>
                  Man Hours
                </label>
                <input
                  type='text'
                  id='manHours'
                  name='manHours'
                  className='w-20 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm'
                  placeholder='##.#'
                  onChange={(e) => {
                    props.setManHours(Number(e.target.value));
                  }}
                />
                <span className='text-gray-500'>man-hours</span>
              </div>
            </div>
          </td>
          <td className='px-4 py-4 text-sm font-medium text-gray-900 text-right'>
            {invoice.lines[0].currencySymbol}
            {(props.manHours * props.auxiliaryServicesPrice).toFixed(2)}
          </td>
          <td className='px-4 py-4 text-sm font-medium text-gray-900 text-right'>
            {invoice.lines[0].currencySymbol}
            {(
              (props.manHours * props.auxiliaryServicesPrice * VAT_RATE) /
              100
            ).toFixed(2)}
          </td>
          <td className='px-4 py-4'>
            <span className='sr-only' />
          </td>
        </tr>
      )}
      {props.additionalServices.length > 0 && (
        <>
          {props.additionalServices.map((service) => {
            return (
              <tr key={service.id} className='hover:bg-gray-50'>
                <td className='px-4 py-4 text-sm text-gray-500'>
                  {service.lineNumber}
                </td>
                <td className='px-4 py-4 text-sm text-gray-900'>
                  <div className='flex items-center justify-between gap-4'>
                    <input
                      type='text'
                      id={`additionalService${service.id}description`}
                      name={`additionalService${service.id}description`}
                      className='flex-1 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm'
                      placeholder='Enter service description...'
                      onChange={(e) => {
                        props.handleAdditionalServiceDescriptionChange(
                          e.target.value,
                          service.id
                        );
                      }}
                    />
                    <button
                      type='button'
                      onClick={() => props.handleDeleteService(service.id)}
                      className='px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors'
                    >
                      Delete
                    </button>
                  </div>
                </td>
                <td className='px-4 py-4 text-sm font-medium text-gray-900'>
                  <div className='flex items-center gap-1'>
                    <span>{invoice.lines[0].currencySymbol}</span>
                    <input
                      type='text'
                      id={`additionalService${service.id}`}
                      name={`additionalService${service.id}`}
                      className='w-24 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm'
                      placeholder='##.##'
                      onChange={(e) => {
                        const updatedServices = props.additionalServices.map(
                          (s) => {
                            if (s.id === service.id) {
                              return {
                                ...s,
                                amount: Number(e.target.value),
                              };
                            }

                            return s;
                          }
                        );

                        props.setAdditionalServices(updatedServices);
                      }}
                    />
                  </div>
                </td>
                <td className='px-4 py-4 text-sm font-medium text-gray-900 text-right'>
                  {invoice.lines[0].currencySymbol}
                  {vatForService(service)}
                </td>
                <td className='px-4 py-4'>
                  <span className='sr-only' />
                </td>
              </tr>
            );
          })}
        </>
      )}
      <tr className='hover:bg-gray-50'>
        <td colSpan={4} className='px-4 py-4 text-center'>
          <button
            type='button'
            onClick={() =>
              props.setAdditionalServices([
                ...props.additionalServices,
                {
                  id: crypto.randomUUID(),
                  lineNumber:
                    invoice.lines.length +
                    props.additionalServices.length +
                    (props.auxiliaryServicesPrice ? 2 : 1),
                  description: '',
                  amount: 0,
                },
              ])
            }
            className='inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors'
          >
            + Add additional service
          </button>
        </td>
        <td className='px-4 py-4'>
          <span className='sr-only' />
        </td>
      </tr>
      <tr className='hover:bg-gray-50'>
        <td className='px-4 py-4 text-sm text-gray-500'>
          {invoice.lines.length +
            props.additionalServices.length +
            (props.auxiliaryServicesPrice ? 2 : 1)}
        </td>
        <td className='px-4 py-4 text-sm text-gray-900'>
          <div className='flex items-center justify-between gap-4'>
            <span>Pallets Provisioned</span>
            <input
              type='text'
              id='palletsProvisioned'
              name='palletsProvisioned'
              className='w-32 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm'
              placeholder='Enter quantity...'
              onChange={(e) => {
                props.setPalletsProvisionedQuantity(Number(e.target.value));
              }}
            />
          </div>
        </td>
        <td className='px-4 py-4 text-sm font-medium text-gray-900 text-right'>
          {invoice.lines[0].currencySymbol}
          {props?.palletsProvisionedCost.toFixed(2)}
        </td>
        <td className='px-4 py-4 text-sm font-medium text-gray-900 text-right'>
          {invoice.lines[0].currencySymbol}
          {props?.palletsProvisionedVat.toFixed(2)}
        </td>
        <td className='px-4 py-4' />
      </tr>
      <tr className='hover:bg-gray-50'>
        <td className='px-4 py-4 text-sm text-gray-500'>
          {invoice.lines.length +
            props.additionalServices.length +
            (props.auxiliaryServicesPrice ? 3 : 2)}
        </td>
        <td className='px-4 py-4 text-sm text-gray-900'>
          <div className='flex items-center justify-between gap-4'>
            <span>Surcharges Applied</span>
            <input
              type='text'
              id='surchargesApplied'
              name='surchargesApplied'
              className='w-32 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm'
              placeholder='Enter quantity...'
              onChange={(e) => {
                props.setSurchargesAppliedQuantity(Number(e.target.value));
              }}
            />
          </div>
        </td>
        <td className='px-4 py-4 text-sm font-medium text-gray-900 text-right'>
          {invoice.lines[0].currencySymbol}
          {props?.surchargesAppliedCost.toFixed(2)}
        </td>
        <td className='px-4 py-4 text-sm font-medium text-gray-900 text-right'>
          {invoice.lines[0].currencySymbol}
          {props?.surchargesAppliedVat.toFixed(2)}
        </td>
        <td className='px-4 py-4' />
      </tr>
      <tr className='hover:bg-gray-50'>
        <td className='px-4 py-4 text-sm text-gray-500'>
          {invoice.lines.length +
            props.additionalServices.length +
            (props.auxiliaryServicesPrice ? 4 : 3)}
        </td>
        <td className='px-4 py-4 text-sm text-gray-900'>
          <div className='flex items-center gap-2'>
            <span>Other:</span>
            <input
              type='text'
              id='otherDescription'
              name='otherDescription'
              className='flex-1 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm'
              placeholder='Add description...'
            />
          </div>
        </td>
        <td className='px-4 py-4 text-sm font-medium text-gray-900'>
          <div className='flex items-center gap-1'>
            <span>{invoice.lines[0].currencySymbol}</span>
            <label className='sr-only' htmlFor='otherAmount'>
              Other Amount
            </label>
            <input
              type='text'
              id='otherAmount'
              name='otherAmount'
              className='w-24 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm'
              placeholder='##.##'
              onChange={(e) => {
                props.setOtherAmount(Number(e.target.value));
              }}
            />
          </div>
        </td>
        <td className='px-4 py-4 text-sm font-medium text-gray-900 text-right'>
          {invoice.lines[0].currencySymbol}
          {props.otherAmountVat.toFixed(2)}
        </td>
        <td className='px-4 py-4'>
          <span className='sr-only' />
        </td>
      </tr>
    </>
  );
}
